/**
 * Utility functions for recurring transactions
 */

export type RecurrenceFrequency = 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly';

/**
 * Calculate all recurrence dates from start to end date
 */
export function calculateRecurrenceDates(
  startDate: string,
  endDate: string | null,
  frequency: RecurrenceFrequency
): string[] {
  const dates: string[] = [];
  const start = new Date(startDate);
  const end = endDate ? new Date(endDate) : null;
  
  // Limit to 10 years if no end date
  const maxDate = end || new Date(start.getFullYear() + 10, start.getMonth(), start.getDate());
  
  let currentDate = new Date(start);
  
  // Add first date
  dates.push(formatDate(currentDate));
  
  // Generate subsequent dates
  while (currentDate < maxDate) {
    currentDate = getNextDate(currentDate, frequency);
    
    if (currentDate <= maxDate) {
      dates.push(formatDate(currentDate));
    }
    
    // Safety limit: max 1000 occurrences
    if (dates.length >= 1000) {
      console.warn('[calculateRecurrenceDates] Reached max limit of 1000 occurrences');
      break;
    }
  }
  
  console.log('[calculateRecurrenceDates] ✅ Generated', dates.length, 'dates from', dates[0], 'to', dates[dates.length - 1]);
  
  return dates;
}

/**
 * Get next date based on frequency
 */
function getNextDate(date: Date, frequency: RecurrenceFrequency): Date {
  const next = new Date(date);
  
  switch (frequency) {
    case 'daily':
      next.setDate(next.getDate() + 1);
      break;
      
    case 'weekly':
      next.setDate(next.getDate() + 7);
      break;
      
    case 'monthly':
      // Handle month increment properly
      const currentMonth = next.getMonth();
      next.setMonth(currentMonth + 1);
      
      // If day changed (e.g., Jan 31 -> Feb 28), set to last day of month
      if (next.getDate() !== date.getDate()) {
        next.setDate(0); // Set to last day of previous month
      }
      break;
      
    case 'quarterly':
      const currentQuarter = next.getMonth();
      next.setMonth(currentQuarter + 3);
      
      // Handle day overflow
      if (next.getDate() !== date.getDate()) {
        next.setDate(0);
      }
      break;
      
    case 'yearly':
      next.setFullYear(next.getFullYear() + 1);
      
      // Handle leap year (Feb 29 -> Feb 28)
      if (next.getDate() !== date.getDate()) {
        next.setDate(0);
      }
      break;
  }
  
  return next;
}

/**
 * Format date as YYYY-MM-DD
 */
function formatDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Get human-readable frequency label
 */
export function getFrequencyLabel(frequency: RecurrenceFrequency): string {
  const labels: Record<RecurrenceFrequency, string> = {
    daily: 'Diária',
    weekly: 'Semanal',
    monthly: 'Mensal',
    quarterly: 'Trimestral',
    yearly: 'Anual',
  };
  
  return labels[frequency];
}

/**
 * Preview recurrence (first 5 dates)
 */
export function previewRecurrence(
  startDate: string,
  frequency: RecurrenceFrequency,
  count: number = 5
): string[] {
  const dates: string[] = [];
  const start = new Date(startDate);
  let currentDate = new Date(start);
  
  dates.push(formatDate(currentDate));
  
  for (let i = 1; i < count; i++) {
    currentDate = getNextDate(currentDate, frequency);
    dates.push(formatDate(currentDate));
  }
  
  return dates;
}

